import { UserApiService, type ApiUser } from '@/services/userApiService'
import type { UserRole } from '@/services/navigationService'

export function readUserDataFromStorage(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem('userData')
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
  } catch (e) {
    console.error('Error parsing user data:', e)
    return {}
  }
}

/** Same rules as login: support/team_manager overrides, then API flags + role. */
export function resolveSessionUserRole(user: Record<string, unknown> | ApiUser): UserRole {
  const data = user as ApiUser
  const rawRole = String(data.role ?? '')
    .trim()
    .toLowerCase()

  if (rawRole === 'support_agent' || data.support_agent === 1 || data.support_agent === true) {
    return 'support_agent'
  }
  if (
    rawRole === 'team_manager' ||
    data.team_manage === 1 ||
    data.team_manage === true ||
    data.team_manager === 1 ||
    data.team_manager === true
  ) {
    return 'team_manager'
  }

  const resolved = UserApiService.getUserRole(data).toLowerCase()
  if (!resolved || resolved === 'user') return 'operator'
  return resolved as UserRole
}

export function syncUserRoleToStorage(user: Record<string, unknown> | ApiUser): UserRole {
  const role = resolveSessionUserRole(user)
  localStorage.setItem('userRole', role)
  return role
}

// Prefer live userData over cached userRole (fetchUserData used to overwrite with a stale role string).
export const getUserRole = (): string => {
  const data = readUserDataFromStorage()
  if (Object.keys(data).length > 0) {
    return resolveSessionUserRole(data)
  }
  const stored = localStorage.getItem('userRole')?.trim().toLowerCase()
  if (stored && stored !== 'user' && stored !== 'null') {
    return stored
  }
  return 'operator'
}

// Utility function to get the appropriate landing page based on user role
export const getRoleLandingPage = (): string => {
  const userRole = getUserRole()

  switch (userRole) {
    case 'recruiter':
      return '/recruiter-dashboard'
    case 'operator':
      return '/operator-dashboard'
    case 'finance':
    case 'finance-staff':
      return '/dashboard-v2'
    case 'analyst':
    case 'analyst_viewer_org':
      return '/analyst-dashboard'
    case 'team_manager':
      return '/team-manager-dashboard'
    case 'driver':
      return '/driver-dashboard'
    case 'developer':
      return '/developer-dashboard'
    case 'client_portal':
      return '/client-portal'
    case 'owner':
    case 'admin':
    case 'manager':
    case 'org_admin':
    case 'support_agent':
      return '/dashboard-v2'
    default:
      return '/dashboard-v2' // Default fallback
  }
}
